import { useState } from 'react'

import isEqual from 'react-fast-compare'

import { UpdateSpaceInput } from '@tribeplatform/gql-client/types'
import { useAdminOnboarding } from '@tribeplatform/react-components/AdminOnboarding'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { Prompt } from '@tribeplatform/react-components/Prompt'
import {
  useUpsertTheme,
  useUpdateNetwork,
  useUpdateSpace,
  useUpdatePostType,
  useNetwork,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { convertAppearanceToNetwork } from '@tribeplatform/react-ui-kit/ThemeProvider'
import { toast } from '@tribeplatform/react-ui-kit/Toast'
import {
  rawSlateUpdatesToDto,
  slateDtoToRaw,
} from '@tribeplatform/slate-kit/utils'

import {
  useCustomizerNavigation,
  useCustomizerSpace,
  useCustomizer,
  useCustomizerTheme,
} from '../hooks/index.js'
import { useCustomizerLogos } from '../hooks/use-customizer-logos.hook.js'
import { getFinalSlateUpdates } from '../utils.js'
import { removeUndefinedKeys } from './utils.js'

export const PreviewSaveButton = () => {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const {
    navigationSlates: { headerDisabled = false, sidebar1Disabled = false },
    images: networkImages,
  } = network
  const { state } = useCustomizer()
  const { spaceId, space, postType, layout, editSpace, editPostType } =
    useCustomizerSpace()
  const { navigationSlates, previewMode } = useCustomizerNavigation()
  const { currentAppearance, appearance } = useCustomizerTheme()
  const { imageIds } = useCustomizerLogos()

  const [saveLoading, setSaveLoading] = useState(false)
  const { mutate: updateNetwork } = useUpdateNetwork()
  const { mutate: updateSpace } = useUpdateSpace()
  const { mutate: updatePostType } = useUpdatePostType()
  const { mutate: upsertTheme } = useUpsertTheme()

  const { completeStep } = useAdminOnboarding()

  const finalUpdate = {
    spaceLayout: layout === space?.layout ? undefined : layout,
    spaceSlate:
      editSpace &&
      rawSlateUpdatesToDto(
        getFinalSlateUpdates(spaceId, slateDtoToRaw(space?.slate), state),
      ),
    postTypeSlate:
      editPostType &&
      rawSlateUpdatesToDto(
        getFinalSlateUpdates(postType.id, slateDtoToRaw(postType.slate), state),
      ),
    navigationSlates: removeUndefinedKeys({
      headerDisabled:
        navigationSlates.headerDisabled === headerDisabled
          ? undefined
          : navigationSlates.headerDisabled,
      sidebar1Disabled:
        navigationSlates.sidebar1Disabled === sidebar1Disabled
          ? undefined
          : navigationSlates.sidebar1Disabled,
      header: rawSlateUpdatesToDto(
        getFinalSlateUpdates(
          'header',
          slateDtoToRaw(navigationSlates?.header),
          state,
        ),
      ),
      sidebar1: rawSlateUpdatesToDto(
        getFinalSlateUpdates(
          'sidebar1',
          slateDtoToRaw(navigationSlates?.sidebar1),
          state,
        ),
      ),
      sidebar2: rawSlateUpdatesToDto(
        getFinalSlateUpdates(
          'sidebar2',
          slateDtoToRaw(navigationSlates?.sidebar2),
          state,
        ),
      ),
      footer: rawSlateUpdatesToDto(
        getFinalSlateUpdates(
          'footer',
          slateDtoToRaw(navigationSlates?.footer),
          state,
        ),
      ),
    }),
    theme: undefined,
    imageIds: undefined,
  }

  if (!isEqual(currentAppearance, appearance)) {
    finalUpdate.theme = appearance
  }

  const networkImageIds = {
    lightLogoId: networkImages?.lightLogo?.id,
    lightMobileLogoId: networkImages?.lightMobileLogo?.id,
    lightFaviconId: networkImages?.lightFavicon?.id,
    darkLogoId: networkImages?.darkLogo?.id,
    darkMobileLogoId: networkImages?.darkMobileLogo?.id,
    darkFaviconId: networkImages?.darkFavicon?.id,
  }
  if (!isEqual(imageIds, networkImageIds)) {
    finalUpdate.imageIds = imageIds
  }

  const saveDisabled =
    !finalUpdate.spaceLayout &&
    !finalUpdate.spaceSlate &&
    !finalUpdate.postTypeSlate &&
    !finalUpdate.navigationSlates &&
    !finalUpdate.theme &&
    !finalUpdate.imageIds

  const onSettled = () => setSaveLoading(false)
  const onSuccess = () => {
    if (finalUpdate.navigationSlates || finalUpdate.theme) {
      completeStep({ step: 'look-and-feel' })
    }
    if (finalUpdate.imageIds) {
      completeStep({ step: 'upload-logo' })
    }
    if (finalUpdate.spaceSlate) {
      completeStep({ step: 'customize-spaces' })
    }

    toast({
      title: $t({
        defaultMessage: 'Your changes were saved.',
        id: 'Generics.YourChangesWereSaved',
      }),
      status: 'success',
    })
  }

  if (previewMode === 'template') {
    return null
  }

  return (
    <>
      <Prompt
        when={!saveDisabled}
        message={$t({
          defaultMessage:
            'If you leave this page, any unsaved changes will be lost.',
          id: 'Admin.Editor.UnsavedChangesAlert',
        })}
      />
      <Button
        size="md"
        disabled={saveDisabled}
        loading={saveLoading}
        onClick={() => {
          if (finalUpdate.navigationSlates) {
            setSaveLoading(true)
            updateNetwork(
              {
                input: { navigationSlates: finalUpdate.navigationSlates },
              },
              {
                onSettled,
                onSuccess,
              },
            )
          } else if (finalUpdate.spaceLayout || finalUpdate.spaceSlate) {
            const input: UpdateSpaceInput = {}
            if (finalUpdate.spaceLayout) {
              input.layout = finalUpdate.spaceLayout
            }
            if (finalUpdate.spaceSlate) {
              input.slate = finalUpdate.spaceSlate
            }
            setSaveLoading(true)
            updateSpace(
              { id: spaceId, input },
              {
                onSettled,
                onSuccess,
              },
            )
          } else if (finalUpdate.postTypeSlate) {
            setSaveLoading(true)
            updatePostType(
              { id: postType.id, input: { slate: finalUpdate.postTypeSlate } },
              {
                onSettled,
                onSuccess,
              },
            )
          } else if (finalUpdate.theme) {
            const input = convertAppearanceToNetwork(finalUpdate.theme, network)
            setSaveLoading(true)
            upsertTheme(
              { input: { ...input, active: true } },
              {
                onSettled,
                onSuccess,
              },
            )
          } else if (finalUpdate.imageIds) {
            setSaveLoading(true)
            updateNetwork(
              {
                input: { imageIds: finalUpdate.imageIds },
              },
              {
                onSettled,
                onSuccess,
              },
            )
          }
        }}
      >
        <T defaultMessage="Save changes" id="Generics.SaveChanges" />
      </Button>
    </>
  )
}
